import React, { useEffect, useState, useRef } from "react"
import { Link } from "gatsby"
import PropTypes from "prop-types"
import { CSSTransition } from "react-transition-group"
import debounce from "lodash.debounce"
import inRange from "lodash.inrange"

import Layout from "../components/layout"
import Hero from "../components/hero"
import SiteHeader from "../components/site-header"
import SiteFooter from "../components/site-footer"
import BaseModal from "../components/base-modal"
import BaseAlert from "../components/base-alert"
import Card from "../components/card"
import dataWorks, { passwordList } from "../constant-works"

import { setParam, getViewport } from "../helper"

const Cards = ({ works }) => {
  if (!works || !works.length) return null
  const cardsRef = useRef(null)
  const [viewHeight, setViewHeight] = useState(0)
  let intersectionObserver = null
  const eventOpt = { view: window, bubbles: true, cancelable: true }
  const eventObserverOn = new Event("observerOn", eventOpt)
  const eventObserverOff = new Event("observerOff", eventOpt)
  const onEntryObserve = (entry) => {
    if (!entry.isIntersecting) return
    const orderCurrent = entry?.target?.dataset?.order || null
    entry.target.parentNode.childNodes.forEach((card) => {
      const cardEl = card.children.namedItem("card")
      if (orderCurrent !== card.dataset.order) {
        cardEl.dispatchEvent(eventObserverOff)
      } else {
        cardEl.dispatchEvent(eventObserverOn)
      }
    })
  }
  const setObserver = (nodeList, cardHeight) => {
    if (!IntersectionObserver) return
    if (intersectionObserver) intersectionObserver.disconnect()
    const offSet = 100
    let marginY = Math.round((viewHeight - cardHeight - offSet) / 2)
    if (marginY <= 0) marginY = 0
    intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach(onEntryObserve)
      },
      { root: null, rootMargin: `-${marginY}px 0px`, threshold: 0.5 }
    )
    nodeList.forEach((node) => {
      intersectionObserver.observe(node)
    })
  }
  const eventOnResize = debounce(() => {
    const { vh } = getViewport()
    const isNotChanged = inRange(vh, viewHeight + 100, viewHeight - 100)
    if (!isNotChanged) setViewHeight(vh)
  }, 300)
  useEffect(() => {
    const { vh } = getViewport()
    setViewHeight(vh)
    window.addEventListener("resize", eventOnResize)
    return () => {
      window.removeEventListener("resize", eventOnResize)
    }
  }, [])

  useEffect(() => {
    const cards = cardsRef?.current?.childNodes
    if (cards && cards.length && viewHeight > 300) {
      const cardHeight = cards[0].clientHeight || 300
      setObserver(cards, cardHeight)
    }
    return () => {
      if (intersectionObserver) intersectionObserver.disconnect()
    }
  }, [viewHeight])
  return (
    <div className="body section touch-py" ref={cardsRef}>
      {works.map((work, index) => (
        <Card {...work} key={index} order={index}></Card>
      ))}
    </div>
  )
}

const ModalPasswordForm = ({ onSubmit }) => {
  const [invalid, setInvalid] = useState(false)
  const handleOnSubmit = (e) => {
    const isValid = onSubmit(e)
    setInvalid(!isValid)
  }
  return (
    <BaseModal hideCloseButton>
      <form
        className="bg-baby-powder text-charcoal shadow-md px-8 pt-6 pb-8 mb-4"
        onSubmit={handleOnSubmit}
      >
        <div className="mb-8 text-charcoal text-xl font-extrabold uppercase">
          require authorization
        </div>
        <div className="mb-8">
          <input
            className="shadow appearance-none border w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            id="pw"
            name="password"
            type="password"
            autoComplete="new-password"
            placeholder="Password"
          />
          {invalid && (
            <label
              htmlFor="pw"
              className="block text-red-500 font-extrabold mt-2"
            >
              Invalid code
            </label>
          )}
        </div>
        <div className="flex flex-col md:flex-row items-center justify-between">
          <button className="button mb-4 w-full md:mb-0 md:w-auto">
            Login
          </button>
          <div>
            <a
              className="inline-block align-baseline font-extrabold text-charcoal text-sm"
              href="mailto:max8hine@gmail.com?subject=I am interested in your work&body=Hi Max,%0D%0A"
            >
              Request to access?
            </a>
            <span> | </span>
            <Link
              className="inline-block align-baseline font-extrabold text-charcoal text-sm"
              to="/"
            >
              Back Home
            </Link>
          </div>
        </div>
      </form>
    </BaseModal>
  )
}

const Works = ({ location }) => {
  const [hasCorrectCode, setHasCorrectCode] = useState(false)

  const updateCode = (code) => {
    if (code && passwordList.includes(code)) {
      setHasCorrectCode(true)
      setParam("pw", code)
      return true
    }
    return false
  }

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    updateCode(urlParams.get("pw"))
  }, [])

  const onSubmit = (e) => {
    e.preventDefault()
    const { password } = e.target
    return updateCode(password.value)
  }

  return (
    <Layout location={location}>
      <SiteHeader location={location}></SiteHeader>
      <div className="main">
        <Hero title="work">
          <p className="lede mb-32">
            Hi there, The page is for the interview and please keep it in the
            interview. You're awesome!
          </p>
        </Hero>
        {!hasCorrectCode && (
          <ModalPasswordForm onSubmit={onSubmit}></ModalPasswordForm>
        )}
        <CSSTransition
          in={hasCorrectCode}
          timeout={500}
          classNames="default"
          unmountOnExit
        >
          <Cards works={dataWorks}></Cards>
        </CSSTransition>
      </div>
      <SiteFooter className="px-8 lg:px-16 py-16"></SiteFooter>
    </Layout>
  )
}

Works.propTypes = {
  location: PropTypes.object.isRequired,
}

export default Works
